import { useEffect, useRef } from "react";
import { EditorState, type Extension } from "@codemirror/state";
import { EditorView, keymap } from "@codemirror/view";
import { basicSetup } from "codemirror";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { oneDark } from "@codemirror/theme-one-dark";

/** Pick a grammar from the file extension; unknown types edit as plain text. */
function languageFor(path: string): Extension[] {
  const ext = path.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "html" || ext === "htm" || ext === "svg") return [html()];
  if (ext === "css") return [css()];
  if (ext === "json") return [json()];
  if (ext === "md") return [markdown()];
  if (["js", "mjs", "jsx", "ts", "tsx"].includes(ext)) {
    return [javascript({ jsx: ext === "jsx" || ext === "tsx", typescript: ext.startsWith("ts") })];
  }
  return [];
}

export default function CanvasEditor({
  path,
  value,
  onChange,
  onSave,
}: {
  path: string;
  value: string;
  onChange(next: string): void;
  onSave(): void;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const view = useRef<EditorView | null>(null);
  // Held in a ref so the CodeMirror extensions never need rebuilding when the
  // parent re-renders with a new closure.
  const handlers = useRef({ onChange, onSave });
  handlers.current = { onChange, onSave };

  // The document is rebuilt per path rather than per keystroke: recreating the
  // state on every value change would drop the cursor and the undo history.
  useEffect(() => {
    if (!host.current) return;
    const dark = document.documentElement.getAttribute("data-theme") !== "daylight";
    view.current = new EditorView({
      parent: host.current,
      state: EditorState.create({
        doc: value,
        extensions: [
          basicSetup,
          ...languageFor(path),
          ...(dark ? [oneDark] : []),
          EditorView.lineWrapping,
          keymap.of([
            {
              key: "Mod-s",
              preventDefault: true,
              run: () => {
                handlers.current.onSave();
                return true;
              },
            },
          ]),
          EditorView.updateListener.of(update => {
            if (update.docChanged) handlers.current.onChange(update.state.doc.toString());
          }),
        ],
      }),
    });
    return () => {
      view.current?.destroy();
      view.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  // Reconcile only when the incoming value genuinely differs — e.g. a revision
  // was reloaded underneath the editor. Echoing our own keystrokes back would
  // reset the selection on every character.
  useEffect(() => {
    const current = view.current;
    if (!current || current.state.doc.toString() === value) return;
    current.dispatch({ changes: { from: 0, to: current.state.doc.length, insert: value } });
  }, [value]);

  return <div className="canvas-editor" ref={host} data-path={path} />;
}
